import { LoteProperties, LoteResponse } from "./LoteResponse";
import { FeatureCollection, MultiPoint, Point, Polygon } from "geojson";
import { BaseItemResponse } from "../BaseItemResponse";

/**
 * Estado de publicación del ortomosaico. El worker no reintenta solo y no hay endpoint
 * de reintento: desde `ERROR` se sale volviendo a llamar a `guardar-ortomosaico` con la
 * misma `zipOrtomosaico`.
 */
export const OrtomosaicoEstado = {
  SIN_GESTIONAR: 0,
  PENDIENTE: 1,
  LISTO: 2,
  ERROR: 3,
} as const;

export type OrtomosaicoEstado = (typeof OrtomosaicoEstado)[keyof typeof OrtomosaicoEstado];

export interface FincaResponse extends BaseItemResponse {
  clienteId: string;
  nombreFinca: string;
  municipioNombre: string | null;
  departamentoNombre: string | null;
  departamentoRegionNombre?: string | null; // "Sur", "Norte", etc.
  tipo?: string | null;                     // "Agricultura" | "Ganadería" | null
  plataformaActiva?: boolean;               // true si fecha vencimiento > ahora
  hectareasFinca: number;
  hectareasProductivas: number;
  tipoFinca: number;
  isAgricultura: boolean;
  isGanaderia: boolean;
  ganaderiaCrecimientoDiarioGr: number;
  ganaderiaDiasDescansoOptimo: number;
  ganaderiaDiasOcupacionOptimo: number;
  ubicacion: Point;
  sateliteAreaGeografica: Polygon;
  /** URL de los tiles publicados; null salvo en `ortomosaicoEstado = LISTO`. Lleva
   *  versión en el path: cambia en cada publicación para que al reemplazar no queden
   *  tiles del anterior visibles. */
  uriOrtomosaico: string | null;
  /** URL del ZIP subido con `fincas/guardar-ortomosaico`; null = nunca se subió uno. */
  zipOrtomosaico: string | null;
  /** Publicación asíncrona: el POST encola y el worker cicla cada 5 min. Sin este
   *  campo, "nunca tuvo", "procesando" y "falló" se veían igual (`uriOrtomosaico: null`). */
  ortomosaicoEstado: OrtomosaicoEstado;
  /** Motivo del fallo; solo con `ortomosaicoEstado = ERROR`. */
  ortomosaicoError: string | null;
  /**
   * Puntos del conteo de árboles. **Solo viene en `GET /terceros/fincas/{id}`**;
   * en el list de fincas y en el detalle del cliente no viaja (son decenas de
   * miles de puntos). Hoy solo tiene valor en las fincas migradas del sistema
   * anterior: lo escribe únicamente el initializer del backend y no existe
   * endpoint para cargarlo, así que toda finca creada después nace en null.
   */
  conteoMultiPoint: MultiPoint | null;
  arbolesMonitorear: number;
  /**
   * Nombres distintos de los cultivos de los lotes no eliminados, ordenados
   * alfabéticamente; `[]` si la finca no tiene lotes o ninguno tiene cultivo.
   * Evita cargar los lotes solo para etiquetar el cultivo de la finca.
   * Viene en `fincas/byClienteId/{id}/list`, `fincas/save` y `clientes/{id}`
   * (y en todo `ClienteFincaResponse`); NO en el contrato REST de NitrofertApi.
   */
  cultivos?: string[];
  lotes: LoteResponse[] | null;
  lotesFeatureCollection: FeatureCollection<Polygon, LoteProperties>;
  /** Denormalizados del dueño. Solo llegan en `GET /terceros/fincas/{id}`. */
  clienteNombre?: string;
  clienteTelefono?: string;
  clienteEmail?: string;
  id: string;
  isEliminado: boolean;
}
